// 버전별 업데이트 로그 주소(/update-logs/v3.4.0) 규칙.
// 원본은 RELEASE.md 하나이고, '# X.Y.Z (날짜)' 머리로 잘린 VersionsInterface 목록을 받는다.

interface VersionLike {
  version: string;
}

/** 버전 문자열을 주소 조각으로 만든다. 3.4.0 → v3.4.0 */
export function versionSlug(version: string): string {
  return `v${version}`;
}

export function versionPath(version: string): string {
  return `/update-logs/${versionSlug(version)}`;
}

/** 주소 조각에서 앞의 v를 떼어 버전 문자열로 돌린다. 'v' 없이 와도 받는다. */
export function slugToVersion(slug: string): string {
  const decoded = decodeURIComponent(slug);
  return /^v\d/i.test(decoded) ? decoded.slice(1) : decoded;
}

/** 주소 조각에 해당하는 버전을 찾는다. 없으면 null */
export function findVersion<T extends VersionLike>(
  versions: T[],
  slug: string
): T | null {
  const wanted = slugToVersion(slug);
  return versions.find((v) => v.version === wanted) ?? null;
}

/**
 * 앞뒤 버전. 목록은 RELEASE.md 순서(최신이 앞)라 가정한다.
 * newer는 목록에서 앞쪽 이웃, older는 뒤쪽 이웃이다.
 */
export function adjacentVersions<T extends VersionLike>(
  versions: T[],
  version: string
): { newer: T | null; older: T | null } {
  const i = versions.findIndex((v) => v.version === version);
  if (i < 0) return { newer: null, older: null };
  return { newer: versions[i - 1] ?? null, older: versions[i + 1] ?? null };
}

/** 'unknown'처럼 머리를 못 읽은 항목은 주소를 만들지 않는다. */
export function isPublishableVersion(v: VersionLike): boolean {
  return v.version !== 'unknown' && /^[\w.\-]+$/.test(v.version);
}

export type UpdateLogRoute =
  | { kind: 'pass' }
  | { kind: 'redirect'; to: string }
  | { kind: 'missing' };

/**
 * 미들웨어용 판정. 페이지 안에서 notFound()·redirect()를 부르면 루트 loading.tsx
 * 때문에 응답이 이미 200으로 흘러나간 뒤라 상태 코드가 바뀌지 않는다.
 * 그래서 렌더 전에 여기서 404와 308을 정한다.
 */
export function classifyUpdateLogPath(
  pathname: string,
  versions: string[]
): UpdateLogRoute {
  const m = pathname.match(/^\/update-logs\/(v?)(\d[^/]*)$/i);
  if (!m) return { kind: 'pass' };
  const version = decodeURIComponent(m[2]);
  if (!versions.includes(version)) return { kind: 'missing' };
  if (m[1] !== 'v') return { kind: 'redirect', to: versionPath(version) };
  return { kind: 'pass' };
}
